<template>
  <div class="px-6 lg:px-10 py-6">
    <h1 class="text-[22px] font-bold text-gray-900 mb-12">Print Check</h1>

    <!-- Loading -->
    <div v-if="loading" class="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
      <div class="max-w-3xl mx-auto space-y-5">
        <div v-for="i in 6" :key="i" class="space-y-2">
          <div class="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-10 w-full bg-gray-100 rounded-xl animate-pulse"></div>
        </div>
      </div>
    </div>

    <div v-else class="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
      <div class="w-full flex items-start gap-12 mt-12">

        <!-- Left controls -->
        <div class="shrink-0 pl-4" :style="{ width: LEFT_W + 'px' }">
          <div class="space-y-4">

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">Select Country</label>
              <select v-model="form.country" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none">
                <option disabled value="">Select country</option>
                <option value="jordan">Jordan</option>
                <option value="ksa">KSA</option>
                <option value="uae">UAE</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">Bank</label>
              <select v-model="form.bankId" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none">
                <option disabled value="">Select bank</option>
                <option v-for="b in banks" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">Date</label>
              <input v-model="form.date" type="date" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none" />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">Title</label>
              <select v-model="form.title" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none">
                <option disabled value="">Select title</option>
                <option>السيدة</option>
                <option>السيد</option>
                <option>شركة</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">To</label>
              <input v-model="form.to" type="text" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none" placeholder="Enter name" />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">Amount</label>
              <input v-model.number="form.amount" type="number" step="0.01" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none" placeholder="0.00" />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">Select Currency</label>
              <select v-model="form.currency" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none">
                <option disabled value="">Select currency</option>
                <option value="JOD">JOD</option>
                <option value="USD">USD</option>
                <option value="SAR">SAR</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">In Words</label>
              <input v-model="form.inWords" type="text" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none" placeholder="المبلغ كتابةً" />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">Payed For</label>
              <input v-model="form.paidFor" type="text" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none" placeholder="Enter reason" />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-1">Crossing</label>
              <select v-model="form.crossing" class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none">
                <option value="">---</option>
                <option value="account">Account Payee Only</option>
              </select>
            </div>

          </div>
        </div>

        <!-- Check preview -->
        <div class="grow flex flex-col items-center mt-6">
          <div
            ref="printRef"
            class="relative mx-auto border border-gray-300 rounded-md bg-white overflow-hidden"
            :style="{ width: CHK_W + 'px', height: CHK_H + 'px' }"
          >
            <img :src="bgSrc" alt="cheque-bg" class="absolute inset-0 w-full h-full object-fill pointer-events-none select-none" />

            <div class="absolute text-black tabular-nums" :style="{ left: '55px', top: '55px', fontSize: '14px', fontWeight:'600' }">
              {{ displayDate }}
            </div>

            <div class="absolute text-black" :style="{ left: (CHK_W/2 - 105) + 'px', top: '65px', width: '210px', textAlign:'center', fontSize:'14px', fontWeight:'600' }">
              يصرف للمستفيد الأول
            </div>

            <div class="absolute text-black" :style="{ right: '56px', top: '100px', maxWidth:'360px', fontSize:'14px', fontWeight:'600'}">
              {{ fullPayTo }}
            </div>

            <!-- مبلغ وقدره -->
            <div id="sumWords" class="absolute text-black text-center"
              :style="{ left: '30px', right: '120px', top: '129px', fontSize:'14px', fontWeight:'600' }">
              {{ form.inWords || placeholderWords }}
            </div>

            <!-- السبب -->
            <div id="paidFor" class="absolute text-black text-center"
              :style="{ left: '90px', right: '70px', top: '157px', fontSize:'14px', fontWeight:'600' }">
              {{ form.paidFor || 'لتسديد مستحقات' }}
            </div>

            <!-- amount boxes -->
            <div class="absolute" :style="{ top: '146px', right: '1px', height:'32px' }">
              <input
                class="tabular-nums"
                :style="{ position:'absolute', top:'0', right:'1px', width:'88px', height:'32px', textAlign:'center', fontSize:'14px', background:'transparent', border:'none' }"
                :value="amountFraction"
                readonly
              />
              <input
                class="tabular-nums"
                :style="{ position:'absolute', top:'0', right:'70px', width:'88px', height:'32px', textAlign:'center', fontSize:'14px', background:'transparent', border:'none' }"
                :value="amountInteger"
                readonly
              />
            </div>

            <div v-if="form.crossing === 'account'" class="absolute inset-0 flex items-center justify-center pointer-events-none select-none" style="transform: rotate(-18deg);">
              <span class="px-6 py-1 border-2 border-gray-700 text-gray-700 font-bold text-[20px] bg-white/70">ACCOUNT PAYEE ONLY</span>
            </div>
          </div>

          <button class="mt-12 h-11 px-6 text-gray-700 hover:bg-gray-50 not-print flex items-center gap-2" @click="printCheque">
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M6 9V2h12v7H6Zm12 3h2a2 2 0 0 1 2 2v5h-5v3H7v-3H2v-5a2 2 0 0 1 2-2h2"/></svg>
            Print check
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useRoute, onBeforeRouteLeave } from 'vue-router'
import api from '@/api'
import checkBg from '@/assets/check.png'

const route = useRoute()

const LEFT_W = 345
const CHK_W  = 624
const CHK_H  = 310

const loading = ref(false)

/* ===== Auth ===== */
const authConfig = computed(() => {
  const token = localStorage.getItem('token')
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} }
})

/* ===== Arabic number-to-words ===== */
const ones = [
  '', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة',
  'عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر',
  'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر',
]
const tensWords     = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون']
const hundredsWords = ['', 'مائة', 'مئتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة']

function toWords(n) {
  if (n === 0) return 'صفر'
  if (n < 20)  return ones[n]
  if (n < 100) {
    const t = Math.floor(n / 10), o = n % 10
    return o ? `${ones[o]} و${tensWords[t]}` : tensWords[t]
  }
  if (n < 1000) {
    const h = Math.floor(n / 100), r = n % 100
    return r ? `${hundredsWords[h]} و${toWords(r)}` : hundredsWords[h]
  }
  if (n < 1_000_000) {
    const th = Math.floor(n / 1000), r = n % 1000
    const w = th === 1 ? 'ألف' : th === 2 ? 'ألفان' : th <= 10 ? `${ones[th]} آلاف` : `${toWords(th)} ألف`
    return r ? `${w} و${toWords(r)}` : w
  }
  if (n < 1_000_000_000) {
    const m = Math.floor(n / 1_000_000), r = n % 1_000_000
    const w = m === 1 ? 'مليون' : m === 2 ? 'مليونان' : `${toWords(m)} مليون`
    return r ? `${w} و${toWords(r)}` : w
  }
  return String(n)
}

function numberToArabicWordsByCurrency(num, currency) {
  if (num === null || num === undefined || num === '' || isNaN(Number(num))) return ''
  const currencyNames = { JOD: 'دينار', USD: 'دولار', SAR: 'ريال' }
  const fractionNames = { JOD: 'فلس',   USD: 'سنت',   SAR: 'هللة' }
  const value   = Math.abs(Number(num))
  const integer = Math.floor(value)
  const decimal = Math.round((value - integer) * 100)
  const cName = currencyNames[currency] || 'دينار'
  const fName = fractionNames[currency] || 'فلس'
  let result = toWords(integer) + ` ${cName}`
  if (decimal > 0) result += ` و${toWords(decimal)} ${fName}`
  result += ' فقط لا غير'
  return result
}

/* ===== Banks ===== */
const banks = ref([])

async function fetchBanks(companyId) {
  try {
    const res  = await api.get('/accounting/banks', authConfig.value)
    const data = Array.isArray(res.data) ? res.data : (res.data?.data || [])
    // Filter by company_id if we have one, otherwise show all
    banks.value = companyId
      ? data.filter(b => String(b.company_id) === String(companyId))
      : data
  } catch (e) {
    console.error(e)
  }
}

/* ===== Form ===== */
const form = reactive({
  country:  '',
  bankId:   '',
  date:     '',
  title:    '',
  to:       '',
  amount:   '',
  currency: '',
  inWords:  '',
  paidFor:  '',
  crossing: '',
})

/* ===== Computed ===== */
const printRef     = ref(null)
const bgDataUrl    = ref('')
const bgSrc        = computed(() => bgDataUrl.value || checkBg)
const fullPayTo    = computed(() => [form.title, form.to].filter(Boolean).join(' ').trim() || '')
const placeholderWords = 'اكتب المبلغ هنا كتابة كاملاً'

const amountInteger  = computed(() => {
  const n = parseFloat(form.amount)
  return isNaN(n) ? '' : String(Math.floor(Math.abs(n)))
})
const amountFraction = computed(() => {
  const n = parseFloat(form.amount)
  return isNaN(n) ? '00' : String(Math.round((Math.abs(n) % 1) * 100)).padStart(2, '0')
})

const displayDate = computed(() => {
  if (!form.date) return '00/00/0000'
  const d  = new Date(form.date)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(-4)
  return `${mm}/${dd}/${yy}`
})

/* ===== Load check data from API ===== */
async function loadCheck(id) {
  loading.value = true
  try {
    const res = await api.get(`/accounting/checks/${id}`, authConfig.value)
    const c   = res.data

    const totalRaw = parseFloat(c.total ?? 0)

    // Fetch banks filtered by this check's company
    await fetchBanks(c.company_id)

    // Fill only what comes from the API — no defaults
    form.date     = c.date             || ''
    form.to       = c.payment_for_name || ''
    form.amount   = totalRaw > 0 ? totalRaw : ''
    form.currency = 'JOD'
    form.paidFor  = c.check_details    || ''
    form.bankId   = c.bank_id          ? String(c.bank_id) : ''

    // In Words: always generate from the numeric total (ignore API total_in_word)
    form.inWords = totalRaw > 0 ? numberToArabicWordsByCurrency(totalRaw, 'JOD') : ''

    // Leave country / title / crossing empty (placeholders)
    form.country  = ''
    form.title    = ''
    form.crossing = ''

  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

/* ===== Print ===== */
function printCheque() {
  const node = printRef.value
  if (!node) return
  const w = window.open('', 'PRINT', 'height=600,width=900')
  if (!w) return

  const html = `
    <html>
      <head>
        <meta charset="utf-8"/>
        <title>Print Check</title>
        <style>
          @page { size: landscape; margin: 0; }
          html, body { margin:0; padding:0; width:100%; height:100%; }
          body { display:flex; justify-content:center; align-items:center; background:#fff; }
          .wrap { width:${CHK_W}px; height:${CHK_H}px; position:relative; }
          * { box-sizing:border-box; font-family:system-ui, -apple-system, Segoe UI, Roboto, Arial; }
          .tabular-nums{ font-variant-numeric: tabular-nums; }
          input[readonly]{ background:transparent!important; border:none; }
          .absolute{ position:absolute; } .inset-0{ top:0; right:0; bottom:0; left:0; }
          .w-full{ width:100%; } .h-full{ height:100%; }
          .object-fill{ object-fit:fill; } .pointer-events-none{ pointer-events:none; }
          .select-none{ user-select:none; } .flex{ display:flex; } .items-center{ align-items:center; }
          .justify-center{ justify-content:center; } .bg-white\/70{ background:rgba(255,255,255,.7); }
          :root{ --shiftWords: 120px; --shiftPaid: 180px; }
          #sumWords, #paidFor { direction: rtl; text-align: right !important; }
          #sumWords { right: calc(120px + var(--shiftWords)) !important; }
          #paidFor  { right: calc(70px  + var(--shiftPaid))  !important; }
        </style>
      </head>
      <body>
        <div class="wrap">${node.innerHTML}</div>
        <script>
          (async function(){
            await new Promise(function(res){
              var imgs = Array.from(document.images||[]);
              if(imgs.length===0) return res();
              var c=0, done=function(){ if(++c>=imgs.length) res(); };
              imgs.forEach(function(img){
                if(img.complete) return done();
                img.addEventListener('load', done);
                img.addEventListener('error', done);
              });
            });
            window.print(); setTimeout(function(){ window.close(); }, 150);
          })();
        <\/script>
      </body>
    </html>`

  w.document.open()
  w.document.write(html)
  w.document.close()
  w.focus()
}

/* ===== Route leave guard ===== */
onBeforeRouteLeave((to) => {
  const checksRoutes = ['Checks', 'CheckUpsertView', 'CheckPrint']
  if (!checksRoutes.includes(to.name)) {
    sessionStorage.removeItem('checks_selected_company_id')
    sessionStorage.removeItem('checks_selected_company_name')
  }
})

/* ===== Lifecycle ===== */
onMounted(async () => {
  // Load bg image as base64 for print
  try {
    const res    = await fetch(checkBg)
    const blob   = await res.blob()
    const reader = new FileReader()
    reader.onload = () => { bgDataUrl.value = reader.result }
    reader.readAsDataURL(blob)
  } catch {
    bgDataUrl.value = ''
  }

  const checkId = route.query.id
  if (checkId) {
    await loadCheck(checkId)
  } else {
    // No id — fetch all banks, leave form empty
    await fetchBanks(null)
  }
})
</script>

<style scoped>
.tabular-nums { font-variant-numeric: tabular-nums; }
input[readonly] { background-color: transparent !important; }
@media print { .not-print { display: none !important; } }
</style>
